import React from 'react';
import { Box, Inline } from '@styleseat/ui';
import { BrandColor, FeatureTheme } from '@styleseat/brand';
import {
  Stack,
  BodyText,
  HeaderText,
  Touchable,
} from '../../../ui';
import { BasicCircleStarIcon } from '../../../ui/Icons';
import { useMount } from '../../../../hooks';
import analytics from '../../../../modules/analytics';
import AboutIncentivesModal from '../AboutIncentivesModal';
import type { DiscountDetails } from '../../../../store/NCDAndDiscountedPros.types';

export type IncentivesBannerProps = {
  providerName?: string | null;
  discountDetails: DiscountDetails;
  renderMoreDetailsCta: (onDismiss: () => void) => React.ReactNode | React.ReactNode[];
  /** Background color of banner */
  color?: string;
};

const IncentivesBanner: React.FCWithChildren<IncentivesBannerProps> = ({
  providerName,
  discountDetails,
  renderMoreDetailsCta = () => null,
  color = FeatureTheme.IncentivesBackgroundColor,
}: IncentivesBannerProps) => {
  const [showMoreDetails, setShowMoreDetails] = React.useState<boolean>(false);

  const onShowMoreDetailsModal = React.useCallback(() => {
    analytics.track('incentives_more_details_clicked');
    setShowMoreDetails(true);
  }, []);

  const onDismissModal = React.useCallback(() => {
    analytics.track('incentives_more_details_dismissed');
    setShowMoreDetails(false);
  }, []);
  const discountText = providerName ? `${providerName} services`
    : 'when you book with an eligible pro';

  useMount(() => {
    analytics.track('incentives_banner_shown');
  });

  const textColor = BrandColor.Metal;
  const backgroundColor = color;

  return (
    <React.Fragment>
      <Inline color={backgroundColor}>
        <Stack
          space={0}
          paddingTop={12}
          paddingBottom={8}
          minWidth="100%"
          maxWidth="100%"
          testID="cip-banner"
          alignItems="center"
        >
          <Inline>
            <Box
              paddingTop={0}
              paddingRight={3}
            >
              <BasicCircleStarIcon
                width={13.75}
                height={13.75}
                color={FeatureTheme.IncentivesColor}
              />
            </Box>
            <HeaderText level={4} color={textColor}>
              {Math.floor(discountDetails.discountPercent || 0)}
              % off -&nbsp;
            </HeaderText>
            <HeaderText level={4} color={textColor}>
              {discountText}
            </HeaderText>
          </Inline>
          <Inline
            space={4}
            alignItems="center"
          >
            <BodyText
              appearance="small"
              color={textColor}
            >
              Discount applied at checkout
            </BodyText>
            <Touchable onClick={onShowMoreDetailsModal}>
              <BodyText
                appearance="small"
                textDecoration="underline"
                color={textColor}
              >
                More Details
              </BodyText>
            </Touchable>
          </Inline>
        </Stack>
      </Inline>
      {showMoreDetails && (
        <AboutIncentivesModal
          onDismiss={onDismissModal}
          renderCTA={() => renderMoreDetailsCta(onDismissModal)}
        />
      )}
    </React.Fragment>
  );
};

export default IncentivesBanner;
